/**
 * A class representing a module in the store.
 */
class StoreModule {
    // The state of the module.
    state;
    /**
     * Creates a new Module object.
     *
     * @param initialState - The initial data for the module.
     */
    constructor(initialState) {
        this.state = { data: initialState, loading: false, error: null };
    }
    // Returns the data in the module.
    data() {
        return this.state.data;
    }
    // Returns the loading status of the data.
    loading() {
        return this.state.loading;
    }
    // Returns the error, if any, that occurred while loading the data.
    error() {
        return this.state.error;
    }
    // Set the loading status of the data.
    SET_LOADING(loading) {
        this.state.loading = loading;
    }
    // Set the data in the module.
    SET_DATA(data) {
        this.state.data = data;
    }
    // Set the error that occurred while loading the data.
    SET_ERROR(error) {
        this.state.error = error;
    }
}
/**
 * A class that represents a store module, which is a piece of data that can be
 * stored and accessed by a component.
 */
class StoreService {
    // The modules in the store, indexed by their keys.
    modules;
    constructor() {
        this.modules = {
            // session
            currentUser: new StoreModule(null),
            // demandes financement
            aggregations: new StoreModule(null),
            totalDemandesFinancement: new StoreModule(0),
            totalSharedDemandesFinancement: new StoreModule(0),
            demandesFinancement: new StoreModule([]),
            sharedDemandesFinancement: new StoreModule([]),
            latestDemandesFinancement: new StoreModule([]),
            latestSharedDemandesFinancement: new StoreModule([]),
            linkedDemandesFinancement: new StoreModule([]),
            publicSettings: new StoreModule(null),
            // opened demandes apprenants
            openedAggregationsApprenants: new StoreModule({}),
            openedTotalDemandesApprenants: new StoreModule(0),
            openedDemandesApprenants: new StoreModule([]),
            openedTotalFilteredDemandesApprenants: new StoreModule(0),
            // closed demandes apprenants
            closedTotalDemandesApprenants: new StoreModule(0),
            closedDemandesApprenants: new StoreModule([]),
            closedAggregationsApprenants: new StoreModule({}),
            closedTotalFilteredDemandesApprenants: new StoreModule(0),
            // contributions VEF with formation not started
            formationNotStartedContributionsVEF: new StoreModule([]),
            formationNotStartedAggregationsContributionsVEF: new StoreModule({}),
            formationNotStartedTotalContributionsVEF: new StoreModule(0),
            formationNotStartedTotalFilteredContributionsVEF: new StoreModule(0),
            // contributions VEF with formation already started
            formationAlreadyStartedContributionsVEF: new StoreModule([]),
            formationAlreadyStartedAggregationsContributionsVEF: new StoreModule({}),
            formationAlreadyStartedTotalContributionsVEF: new StoreModule(0),
            formationAlreadyStartedTotalFilteredContributionsVEF: new StoreModule(0),
        };
    }
    get(moduleName) {
        return this.modules[moduleName];
    }
    /**
     * Get current user
     */
    getCurrentUser() {
        return this.modules.currentUser;
    }
    /**
     * Get aggregation
     */
    getAggregation() {
        return this.modules.aggregations;
    }
    /**
     * Get total demandes
     */
    getTotalDemandesFinancement() {
        return this.modules.totalDemandesFinancement;
    }
    /**
     * Get total shared demandes
     */
    getTotalSharedDemandesFinancement() {
        return this.modules.totalSharedDemandesFinancement;
    }
    /**
     * Get demandes
     */
    getDemandesFinancement() {
        return this.modules.demandesFinancement;
    }
    /**
     * Get shared demandes
     */
    getSharedDemandesFinancement() {
        return this.modules.sharedDemandesFinancement;
    }
    /**
     * Get latest demandes
     */
    getLatestDemandesFinancement() {
        return this.modules.latestDemandesFinancement;
    }
    /**
     * Get latest shared demandes
     */
    getLatestSharedDemandesFinancement() {
        return this.modules.latestSharedDemandesFinancement;
    }
    /**
     * Get linkedDemandesFinancement
     */
    getLinkedDemandesFinancement() {
        return this.modules.linkedDemandesFinancement;
    }
    /**
     * Get public settings
     */
    getPublicSettings() {
        return this.modules.publicSettings;
    }
    /**
     * Get opened demandes apprenants aggregations
     */
    getOpenedAggregationsApprenants() {
        return this.modules.openedAggregationsApprenants;
    }
    /**
     * Get opened demandes apprenants
     */
    getOpenedDemandesApprenants() {
        return this.modules.openedDemandesApprenants;
    }
    /**
     * Get opened demandes apprenants total
     */
    getTotalOpenedDemandesApprenants() {
        return this.modules.openedTotalDemandesApprenants;
    }
    /**
     * Get opened demandes apprenants total filtered
     */
    getTotalFilteredOpenedDemandesApprenants() {
        return this.modules.openedTotalFilteredDemandesApprenants;
    }
    /**
     * Get closed demandes apprenants aggregations
     */
    getClosedAggregationsApprenants() {
        return this.modules.closedAggregationsApprenants;
    }
    /**
     * Get closed demandes apprenants total
     */
    getTotalClosedDemandesApprenants() {
        return this.modules.closedTotalDemandesApprenants;
    }
    /**
     * Get closed demandes apprenants
     */
    getClosedDemandesApprenants() {
        return this.modules.closedDemandesApprenants;
    }
    /**
     * Get closed demandes apprenants total filtered
     */
    getTotalFilteredClosedDemandesApprenants() {
        return this.modules.openedTotalFilteredDemandesApprenants;
    }
    /**
     * Get contributions VEF with formation that has not started yet
     */
    getFormationNotStartedContributionsVEF() {
        return this.modules.formationNotStartedContributionsVEF;
    }
    /**
     * Get contributions VEF with formation that already started
     */
    getFormationAlreadyStartedContributionsVEF() {
        return this.modules.formationAlreadyStartedContributionsVEF;
    }
    /**
     * Get formation not started aggregations
     */
    getFormationNotStartedAggregationsContributionsVEF() {
        return this.modules.formationNotStartedAggregationsContributionsVEF;
    }
    /**
     * Get formation already started aggregations
     */
    getFormationAlreadyStartedAggregationsContributionsVEF() {
        return this.modules.formationAlreadyStartedAggregationsContributionsVEF;
    }
    /**
     * Get total contributions VEF with formation that has not started yet
     */
    getTotalFormationNotStartedContributionsVEF() {
        return this.modules.formationNotStartedTotalContributionsVEF;
    }
    /**
     * Get total contributions VEF with formation that already started
     */
    getTotalFormationAlreadyStartedContributionsVEF() {
        return this.modules.formationAlreadyStartedTotalContributionsVEF;
    }
    /**
     * Get total filtered contributions VEF with formation that has not started yet
     */
    getTotalFilteredFormationNotStartedContributionsVEF() {
        return this.modules.formationNotStartedTotalFilteredContributionsVEF;
    }
    /**
     * Get total filtered contributions VEF with formation that already started
     */
    getTotalFilteredFormationAlreadyStartedContributionsVEF() {
        return this.modules.formationAlreadyStartedTotalFilteredContributionsVEF;
    }
}
export { StoreModule, StoreService };
//# sourceMappingURL=store-service.js.map